import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { BellOff, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ConversationList } from '@up2d8/contracts';
import { Avatar, Card, EmptyState, LoadingState } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { DeskShell } from './desk/desk-shell';
import { DeskMessagesPage } from './desk/desk-messages-page';
import { CrossTenantSection } from './cross-tenant/cross-tenant-section';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { uiLocale } from '../../shared/format';

/**
 * De gesprekken, in twee indelingen (Stap 34c; het bureauscherm sinds BER-5).
 *
 * Op een bureauscherm is dit het drieluik uit de replica-spec: gesprekkenlijst, gesprek,
 * groepsinfo. Daaronder blijft het de lijst zoals ze was — één kolom, kaarten, een rij per
 * gesprek.
 *
 * **Eén schil en twee inhouden, geen twee schillen.** Allebei zitten ze in de DOM en CSS kiest;
 * dat is dezelfde regel als in `DeskShell` zelf. De testhaken verschillen daarom wél per
 * indeling — `conversation` op de telefoon, `chat-row` op het bureauscherm. Twee keer dezelfde
 * haak in één DOM maakt elke test dubbelzinnig, ook als er maar één van de twee zichtbaar is, en
 * `messaging.spec.ts` zet met opzet een venster van 390 px neer.
 *
 * "Gesprekken" en niet "Berichten" in de navigatie: in deze app zijn berichten al iets anders — de
 * bedrijfsberichten op de tijdlijn. Twee betekenissen voor hetzelfde woord in dezelfde zijbalk is
 * hoe mensen op de verkeerde plek gaan zoeken. De **kop in het paneel** mag wel "Berichten" zijn,
 * zoals de mockup (besluit van 6 oktober).
 */
export function AppConversationsPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const tabs = useAppTabs();

  const conversations = useQuery({
    queryKey: ['conversations'],
    queryFn: async (): Promise<ConversationList> => {
      const { data, error } = await api.GET('/api/v1/conversations');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as ConversationList;
    },
  });

  const items = conversations.data?.items ?? [];

  return (
    <DeskShell
      fill
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="messages"
      renderLink={renderAppTabLink}
      messageCount={conversations.data?.totalUnread ?? null}
    >
      {/* --- Bureauscherm: het drieluik (replica-spec §2) --- */}
      <div className="hidden min-h-0 desk:flex desk:h-full desk:flex-col">
        <CrossTenantSection />
        <div className="min-h-0 flex-1">
          <DeskMessagesPage />
        </div>
      </div>

      {/* --- Telefoon: de lijst zoals ze was --- */}
      <div className="flex flex-col gap-element desk:hidden">
        <h1 className="font-heading text-page-title font-semibold text-text-primary">
          {t('messaging.title')}
        </h1>

        <CrossTenantSection />

        {conversations.isPending ? (
          <LoadingState rows={4} label={t('common.loading')} />
        ) : items.length === 0 ? (
          <Card>
            <EmptyState title={t('messaging.empty')} description={t('messaging.emptyHint')} />
          </Card>
        ) : (
          <Card>
            <ul className="flex flex-col" data-testid="conversation-list">
              {items.map((conversation) => (
                <li key={conversation.id}>
                  <Link
                    to="/app/gesprekken/$conversationId"
                    params={{ conversationId: conversation.id }}
                    data-touch-target=""
                    data-testid="conversation"
                    className="flex min-h-touch items-center gap-element border-b border-border-light py-2 last:border-b-0 hover:bg-surface-sunk"
                  >
                    <Avatar name={conversation.title} size="md" />

                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate font-heading text-content font-medium text-text-primary">
                          {conversation.title}
                          {conversation.tenantName && (
                            <span className="ml-1.5 font-body text-caption font-normal text-text-muted">
                              {t('messaging.viaTenant', { tenant: conversation.tenantName })}
                            </span>
                          )}
                        </span>
                        {conversation.lastMessageAt && (
                          <span className="shrink-0 text-caption text-text-muted">
                            {new Date(conversation.lastMessageAt).toLocaleDateString(uiLocale(), {
                              day: 'numeric',
                              month: 'short',
                            })}
                          </span>
                        )}
                      </div>
                      {conversation.lastMessagePreview && (
                        <p className="truncate text-small text-text-muted">
                          {conversation.lastMessagePreview}
                        </p>
                      )}
                    </div>

                    {conversation.muted && (
                      <BellOff
                        aria-label={t('messaging.mutedLabel')}
                        className="h-4 w-4 shrink-0 text-text-muted"
                      />
                    )}
                    {conversation.unreadCount > 0 && (
                      <span
                        data-testid="conversation-unread"
                        className="shrink-0 rounded-pill bg-brand-orange px-2 text-caption font-semibold text-text-on-dark"
                      >
                        {conversation.unreadCount}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Op een telefoon loopt de weg naar een nieuw gesprek via de collega-lijst: je kiest een
            mens, niet een gespreksvorm. Op desktop staat de knop in de kop van het paneel. */}
        <Link to="/app/collegas">
          <Card className="flex items-center gap-element hover:border-brand-primary">
            <MessageSquare aria-hidden className="h-5 w-5 text-brand-primary" />
            <span className="text-content text-text-primary">{t('messaging.startHint')}</span>
          </Card>
        </Link>
      </div>
    </DeskShell>
  );
}
